import type { AttendeePaymentLedgerRow } from './sessionAttendeePayments';
import {
  isAttendeeInFeeTotals,
  normalizedAttendeeStatus,
  type SessionAttendeeStatus,
} from './sessionAttendeeStatus';

export type AttendeePaymentMethod = 'stripe' | 'revolut' | 'sumup' | 'paypal' | 'bank' | 'cash';

const METHODS: AttendeePaymentMethod[] = ['stripe', 'revolut', 'sumup', 'paypal', 'bank', 'cash'];

export function sumLedgerRawPaid(rows: AttendeePaymentLedgerRow[]): number {
  return rows.reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0);
}

export function attendeeBalanceEuro(fee: number, rawPaid: number): number {
  if (fee > 0) return fee - rawPaid;
  return -rawPaid;
}

export function attendeeOverpaymentEuro(fee: number, rawPaid: number): number {
  if (fee <= 0) return Math.max(0, rawPaid);
  return Math.max(0, rawPaid - fee);
}

export function deriveAttendeePaymentMix(
  fee: number,
  rawPaid: number
): 'paid' | 'half_paid' | 'unpaid' {
  if (fee > 0 && rawPaid >= fee - 0.005) return 'paid';
  if (rawPaid > 0) return 'half_paid';
  return 'unpaid';
}

export type SessionFinanceOverview = {
  totalFee: number;
  totalCollected: number;
  totalPrepaid: number;
  totalDirectCollected: number;
  totalOutstanding: number;
  totalOverpayment: number;
  profitTotal: number;
  paidCount: number;
  halfPaidCount: number;
  unpaidCount: number;
  feeTotalSeatCount: number;
  methodTotals: Record<AttendeePaymentMethod, number>;
  methodCounts: Record<AttendeePaymentMethod, number>;
};

export function emptyMethodRecord(): Record<AttendeePaymentMethod, number> {
  return { stripe: 0, revolut: 0, sumup: 0, paypal: 0, bank: 0, cash: 0 };
}

function isPrepaidStepType(stepType: string | null | undefined): boolean {
  const s = String(stepType || 'other');
  return s === 'deposit' || s === 'other';
}

function isOnSiteStepType(stepType: string | null | undefined): boolean {
  return String(stepType || '') === 'on_day';
}

/**
 * Session finance overview:
 * - Total fees, outstanding, overpayment & payment mix: active roster only (booked / completed)
 * - Collected, prepaid, on-site & by-method: all payment ledger rows (incl. removed)
 */
export function computeSessionFinanceOverview(args: {
  attendees: Array<{ id: string; status?: string | null }>;
  paymentTimelineByAttendee: Record<string, AttendeePaymentLedgerRow[]>;
  feeForAttendee: (attendeeId: string) => number;
  collectedProfit: number;
}): SessionFinanceOverview {
  let totalFee = 0;
  let totalOutstanding = 0;
  let totalOverpayment = 0;
  let paidCount = 0;
  let halfPaidCount = 0;
  let unpaidCount = 0;
  let feeTotalSeatCount = 0;

  args.attendees.forEach((attendee) => {
    const ledger = args.paymentTimelineByAttendee[attendee.id] || [];
    const rawPaid = sumLedgerRawPaid(ledger);
    const status = normalizedAttendeeStatus(
      (attendee.status || 'booked') as SessionAttendeeStatus
    );
    if (!isAttendeeInFeeTotals({ status })) return;

    const fee = args.feeForAttendee(attendee.id);
    feeTotalSeatCount += 1;
    totalFee += fee;
    totalOutstanding += fee > 0 ? Math.max(0, fee - rawPaid) : 0;
    totalOverpayment += attendeeOverpaymentEuro(fee, rawPaid);

    const mix = deriveAttendeePaymentMix(fee, rawPaid);
    if (mix === 'paid') paidCount += 1;
    else if (mix === 'half_paid') halfPaidCount += 1;
    else unpaidCount += 1;
  });

  const methodTotals = emptyMethodRecord();
  const methodCounts = emptyMethodRecord();
  let totalCollected = 0;
  let totalPrepaid = 0;
  let totalDirectCollected = 0;

  args.attendees.forEach((attendee) => {
    (args.paymentTimelineByAttendee[attendee.id] || []).forEach((row) => {
      const amount = Math.max(0, Number(row.amount || 0));
      if (amount <= 0) return;
      totalCollected += amount;
      if (isPrepaidStepType(row.stepType)) totalPrepaid += amount;
      if (isOnSiteStepType(row.stepType)) totalDirectCollected += amount;
      const method = (METHODS.includes(row.method as AttendeePaymentMethod)
        ? row.method
        : 'cash') as AttendeePaymentMethod;
      methodTotals[method] += amount;
      methodCounts[method] += 1;
    });
  });

  return {
    totalFee,
    totalCollected,
    totalPrepaid,
    totalDirectCollected,
    totalOutstanding,
    totalOverpayment,
    profitTotal: args.collectedProfit + totalOutstanding,
    paidCount,
    halfPaidCount,
    unpaidCount,
    feeTotalSeatCount,
    methodTotals,
    methodCounts,
  };
}

/** Roster paid column: uncapped ledger sum (overpayments visible). */
export function rosterPaidEuro(
  attendee: { amountPaid?: number | null },
  ledgerRows: AttendeePaymentLedgerRow[]
): number {
  if (ledgerRows.length > 0) return sumLedgerRawPaid(ledgerRows);
  return Math.max(0, Number(attendee.amountPaid || 0));
}
